import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Slider from '@mui/material/Slider';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CheckIcon from '@mui/icons-material/Check';
import PaletteIcon from '@mui/icons-material/Palette';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SENSOR_TYPES, SENSOR_COLORS, FIRESTORE_PATHS } from '../context/SensorConfigContext';
import { useGrow } from '../context/GrowContext';

// Helper functions for color conversion
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

// Color Picker Screen Component
function ColorPickerScreen({ color, onColorChange, onBack }) {
  const [hexInput, setHexInput] = React.useState(color);
  const [rgb, setRgb] = React.useState(() => hexToRgb(color));

  const handlePresetClick = (presetColor) => {
    setHexInput(presetColor);
    setRgb(hexToRgb(presetColor));
    onColorChange(presetColor);
  };

  const handleHexChange = (e) => {
    const value = e.target.value;
    setHexInput(value);
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      setRgb(hexToRgb(value));
      onColorChange(value);
    }
  };

  const handleRgbChange = (channel, value) => {
    const newRgb = { ...rgb, [channel]: value };
    setRgb(newRgb);
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHexInput(newHex);
    onColorChange(newHex);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '0.8rem' }}>
            Choose Color
          </Typography>
        </Stack>
        {/* Current color preview */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: hexInput,
            border: '2px solid',
            borderColor: 'divider',
          }}
        />
      </Stack>

      {/* Preset Colors */}
      <Box sx={{ mb: 1.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block', fontSize: '0.65rem' }}>
          Presets
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {SENSOR_COLORS.map((c) => (
            <Box
              key={c.value}
              onClick={() => handlePresetClick(c.value)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: c.value,
                cursor: 'pointer',
                border: color === c.value ? '2px solid white' : '2px solid transparent',
                boxShadow: color === c.value ? `0 0 0 2px ${c.value}` : 'none',
                transition: 'transform 0.1s',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Hex Input */}
      <Box sx={{ mb: 1.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block', fontSize: '0.65rem' }}>
          Hex Color
        </Typography>
        <TextField
          size="small"
          value={hexInput}
          onChange={handleHexChange}
          fullWidth
          placeholder="#000000"
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
              py: 0.75,
              fontFamily: 'monospace',
            },
          }}
        />
      </Box>

      {/* RGB Sliders */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block', fontSize: '0.65rem' }}>
          RGB
        </Typography>
        <Stack spacing={1}>
          {/* Red */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
            <Slider
              value={rgb.r}
              onChange={(_, v) => handleRgbChange('r', v)}
              min={0}
              max={255}
              size="small"
              sx={{
                color: '#ef4444',
                '& .MuiSlider-thumb': { width: 14, height: 14 },
              }}
            />
            <Typography sx={{ minWidth: 24, fontSize: '0.6rem', fontFamily: 'monospace', textAlign: 'right' }}>
              {rgb.r}
            </Typography>
          </Stack>
          {/* Green */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
            <Slider
              value={rgb.g}
              onChange={(_, v) => handleRgbChange('g', v)}
              min={0}
              max={255}
              size="small"
              sx={{
                color: '#22c55e',
                '& .MuiSlider-thumb': { width: 14, height: 14 },
              }}
            />
            <Typography sx={{ minWidth: 24, fontSize: '0.6rem', fontFamily: 'monospace', textAlign: 'right' }}>
              {rgb.g}
            </Typography>
          </Stack>
          {/* Blue */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6', flexShrink: 0 }} />
            <Slider
              value={rgb.b}
              onChange={(_, v) => handleRgbChange('b', v)}
              min={0}
              max={255}
              size="small"
              sx={{
                color: '#3b82f6',
                '& .MuiSlider-thumb': { width: 14, height: 14 },
              }}
            />
            <Typography sx={{ minWidth: 24, fontSize: '0.6rem', fontFamily: 'monospace', textAlign: 'right' }}>
              {rgb.b}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

function SensorSettingsOverlay({ sectionId, sensor, growId, onClose }) {
  const { grows, updateGrowDashboardConfig } = useGrow();
  const [name, setName] = React.useState(sensor.name || '');
  const [size, setSize] = React.useState(sensor.size || 'small');
  const [dataPath, setDataPath] = React.useState(sensor.dataPath || '');
  const [color, setColor] = React.useState(sensor.color || SENSOR_COLORS[0].value);
  const [showColorPicker, setShowColorPicker] = React.useState(false);

  // Update sensor in grow's config
  const updateSensor = (updates) => {
    if (!growId) return;
    const currentGrow = grows.find(g => g.id === growId);
    const sections = currentGrow?.dashboardConfig?.sections || [];
    const updatedSections = sections.map(s => {
      if (s.id === sectionId) {
        return {
          ...s,
          sensors: s.sensors.map(sen =>
            sen.id === sensor.id ? { ...sen, ...updates } : sen
          ),
        };
      }
      return s;
    });
    updateGrowDashboardConfig(growId, { sections: updatedSections });
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);
    updateSensor({ name: newName });
  };

  const handleSizeToggle = () => {
    const newSize = size === 'small' ? 'big' : 'small';
    setSize(newSize);
    updateSensor({ size: newSize });
  };

  const handleDataPathSelect = (path) => {
    setDataPath(path);
    updateSensor({ dataPath: path });
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    updateSensor({ color: newColor });
  };

  const sensorTypeInfo = SENSOR_TYPES[sensor.type];

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'background.paper',
        borderRadius: 'inherit',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {showColorPicker ? (
        <ColorPickerScreen
          color={color}
          onColorChange={handleColorChange}
          onBack={() => setShowColorPicker(false)}
        />
      ) : (
        <>
          {/* Header with gear icon, size toggle, color, and close buttons */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexShrink: 0 }}>
            {/* Gear icon */}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <SettingsIcon sx={{ fontSize: 16 }} />
            </Box>

            {/* Action buttons */}
            <Stack direction="row" spacing={0.5}>
              {/* Size toggle - single button */}
              <Tooltip title={size === 'small' ? 'Switch to large' : 'Switch to small'}>
                <IconButton
                  size="small"
                  onClick={handleSizeToggle}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <AspectRatioIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {/* Color picker button */}
              <Tooltip title="Change color">
                <IconButton
                  size="small"
                  onClick={() => setShowColorPicker(true)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: color,
                    color: 'white',
                    '&:hover': {
                      bgcolor: color,
                      opacity: 0.8,
                    },
                  }}
                >
                  <PaletteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {/* Close button */}
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Size indicator */}
          <Box sx={{ mb: 1, flexShrink: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              Size: <strong>{size === 'small' ? 'Small' : 'Large'}</strong>
            </Typography>
          </Box>

          {/* Settings Form */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
            {/* Sensor Name */}
            <Box sx={{ flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block', fontSize: '0.65rem' }}>
                Name
              </Typography>
              <TextField
                size="small"
                value={name}
                onChange={handleNameChange}
                fullWidth
                placeholder="Sensor name"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.75rem',
                    py: 0.75,
                  },
                }}
              />
            </Box>

            {/* Data Source - Scrollable List */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block', fontSize: '0.65rem', flexShrink: 0 }}>
                Data Source
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: 80,
                }}
              >
                <List dense disablePadding>
                  {FIRESTORE_PATHS.map((path) => (
                    <ListItemButton
                      key={path.value}
                      selected={dataPath === path.value}
                      onClick={() => handleDataPathSelect(path.value)}
                      sx={{ py: 0.25, minHeight: 26 }}
                    >
                      <ListItemText
                        primary={path.label}
                        primaryTypographyProps={{ fontSize: '0.6rem', fontFamily: 'Inter, sans-serif' }}
                      />
                      {dataPath === path.value && <CheckIcon sx={{ fontSize: 12, color: 'primary.main' }} />}
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Box>

            {/* Sensor Type Info */}
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 0.75, flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', fontSize: '0.55rem' }}>
                Type: {sensorTypeInfo?.label || sensor.type} | Range: {sensorTypeInfo?.min}-{sensorTypeInfo?.max} {sensorTypeInfo?.unit}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

SensorSettingsOverlay.propTypes = {
  sectionId: PropTypes.string.isRequired,
  sensor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    name: PropTypes.string,
    size: PropTypes.string,
    dataPath: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
  growId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default SensorSettingsOverlay;
